import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAlertGroups, loadParentAlertGroups, deleteAlertGroup, loadAssignedAlertGroupList } from "../../../redux/actions/alertDeviceActions";
import "./AlertGroupsView.css";
import BreadCrumb from '../../shared/BreadCrumbs';

const ENTRIES_OPTIONS = [10, 25, 50];

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// --- Group Row ---
function GroupRow({ group, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = () => {
    if (confirmDelete) {
      onDelete(group.id, group.alertDeviceId);
    } else {
      setConfirmDelete(true);
    }
  };

  return (
    <div className="group-row">
      <div className="group-name">
        <a href={group.editUrl} className="group-name-link">{group.name}</a>
      </div>
      <div className="group-cell group-cell-count">{group.totalCount}</div>
      <div className="group-cell group-date">{formatDate(group.lastUpdated)}</div>
      <div className="group-actions">
        {confirmDelete ? (
          <span className="confirm-wrap">
            <span className="confirm-text">Sure?</span>
            <button className="btn-confirm" onClick={handleDelete}>Yes</button>
            <button className="btn-cancel" onClick={() => setConfirmDelete(false)}>No</button>
          </span>
        ) : (
          <button className="btn-delete" onClick={handleDelete}>Delete</button>
        )}
      </div>
    </div>
  );
}

// --- Group Table ---
function GroupTable({ title, groups, onDelete }) {
  return (
    <div className="table-card">
      <div className="table-head">
        <span>{title} — Total: {groups.length}</span>
        <span className="head-badge">{groups.length} groups</span>
      </div>
      <div className="col-header">
        <span>Group name ↕</span>
        <span>Total count</span>
        <span>Last updated</span>
        <span className="col-header-right" style={{ textAlign: "left", marginLeft: "10px" }}>Manage</span>
      </div>
      {groups.length === 0 ? (
        <div className="empty-state">No groups found.</div>
      ) : (
        groups.map((g) => (
          <GroupRow key={g.id} group={g} onDelete={onDelete} />
        ))
      )}
    </div>
  );
}

// --- Pagination ---
function Pagination({ page, totalPages, entriesPerPage, onPageChange, onEntriesChange }) {
  return (
    <div className="pagination-bar">
      <div className="meta-right">
        <span className="meta-label">Show</span>
        <select
          className="select"
          value={entriesPerPage}
          onChange={(e) => onEntriesChange(Number(e.target.value))}
        >
          {ENTRIES_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span className="meta-label">entries &nbsp; Page</span>
        <button
          className="pager-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          ‹
        </button>
        <span className="meta-label">{page}</span>
        <button
          className="pager-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          ›
        </button>
        <span className="meta-label">of {totalPages}</span>
      </div>
    </div>
  );
}


export default function AlertGroupsPage({
  onCreateMaster,
  onCreateAlert,
}) {
  const dispatch = useDispatch();
  const alertDeviceDetails = useSelector((state) => state.devices.alertDeviceDetails);
  const assignedAlertGroups = useSelector((state) => state.devices.assignedAlertGroups);
  const parentAlertGroups = useSelector((state) => state.devices.parentAlertGroups);
  const loading = useSelector((state) => !!state.ui.loading);

  const BreadCrumbArr = [
    { link: "/main/device_main.php", display: "Dashboard", type: 'php' },
    { link: "/admin/adminscr.php", display: "Administration", type: 'php' },
    { link: "/administration/AlertGroupsView/", display: "Alert Groups", type: 'react' },
  ];

  const handleDeleteAlertGroup = (id, alertDeviceId) => {
    dispatch(deleteAlertGroup(id, alertDeviceId));
  };

  const groups = useMemo(
    () =>
      Object.values(alertDeviceDetails)
        .filter((d) => d.alert_device_type === 13)
        .map((g) => {
          const junction = Object.values(assignedAlertGroups).find(
            (r) => r.alert_group_id === g.alert_device_id
          );
          const matchingJunctions = Object.values(assignedAlertGroups).filter(
            (r) => r.alert_group_id === g.alert_device_id
          );
          const assignedCount = matchingJunctions.length;
          const latestModified = matchingJunctions.reduce((latest, r) =>
            r.modified > latest ? r.modified : latest, g.modified ?? ''
          );
          return {
            id: junction?.seq ?? g.alert_device_id,
            alertDeviceId: g.alert_device_id,
            editUrl: `/admin/alert_group_edit.php?edit_row[alert_device_id]=${g.alert_device_id}`,
            name: g.alert_description ?? '',
            totalCount: assignedCount,
            lastUpdated: latestModified,
          };
        }),
    [alertDeviceDetails, assignedAlertGroups]
  );

  const [filterType, setFilterType] = useState("All");
  const [search, setSearch] = useState("");
  const [alertPage, setAlertPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [masterPage, setMasterPage] = useState(1);
  const [masterEntriesPerPage, setMasterEntriesPerPage] = useState(10);
  

  useEffect(() => {
    dispatch(loadAlertGroups());
    dispatch(loadAssignedAlertGroupList());
    dispatch(loadParentAlertGroups());
  }, [dispatch]);

  const handleReset = () => {
    setSearch("");
    setFilterType("All");
    setAlertPage(1);
    setMasterPage(1);
  };

  const filteredAlertGroups = useMemo(() => {
    return groups.filter((g) =>
      g.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [groups, search]);

  const filteredMasterGroups = useMemo(() => {
    const uniqueGroupIds = [...new Set(
      Object.values(parentAlertGroups).map((r) => r.parent_alert_group_id)
    )];
    return uniqueGroupIds
      .map((id) => {
        const detail = alertDeviceDetails[id];
        const matchingParents = Object.values(parentAlertGroups).filter(
          (r) => r.parent_alert_group_id === id
        );
        const parentCount = matchingParents.length;
        const latestModified = matchingParents.reduce((latest, r) =>
          r.modified > latest ? r.modified : latest, detail?.modified ?? ''
        );
        return {
          id,
          alertDeviceId: id,
          editUrl: `/admin/alert_group_edit.php?edit_row[alert_device_id]=${id}&group_type=parent`,
          name: detail?.alert_description ?? `Group ${id}`,
          totalCount: parentCount,
          lastUpdated: latestModified,
        };
      })
      .filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));
  }, [parentAlertGroups, alertDeviceDetails, search]);

  const masterTotalPages = Math.max(1, Math.ceil(filteredMasterGroups.length / masterEntriesPerPage));

  const paginatedMasterGroups = filteredMasterGroups.slice(
   (masterPage - 1) * masterEntriesPerPage,
   masterPage * masterEntriesPerPage
  );

  const totalPages = Math.max(1, Math.ceil(filteredAlertGroups.length / entriesPerPage));

  const paginatedAlertGroups = filteredAlertGroups.slice(
    (alertPage - 1) * entriesPerPage,
    alertPage * entriesPerPage
  );

  const showMaster = filterType === "All" || filterType === "Master groups";
  const showAlert  = filterType === "All" || filterType === "Alert groups";

  if (loading) {
    return <div className="loading-state">Loading groups...</div>;
  }

  return (
    <>
      <div className="ag-breadcrumb">
        <BreadCrumb linkArr={BreadCrumbArr}></BreadCrumb>
      </div>

      <div className="ag-page">

      {/* Top bar */}
      <div className="top-bar">
        <span className="select-label">Group selection</span>
        <span className="select-sub">Select a group for detailed view</span>
        <select
          className="select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option>All</option>
          <option>Master groups</option>
          <option>Alert groups</option>
        </select>
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input
            className="search-input"
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setAlertPage(1); }}
          />
        </div>
        <button className="btn-reset" onClick={handleReset}>Reset</button>
      </div>

      {/* Master groups */}
      {showMaster && (
        <>
          <div className="section-header">
            <span className="section-title">Master alert groups</span>
            <button className="btn-create" onClick={() => window.location.href = '/admin/alert_group_edit.php?group_type=parent'}>
              + New Master Group
            </button>
          </div>
           <div className="meta-title">
            Group name details — Total groups: {filteredMasterGroups.length}
            </div>
            <Pagination
            page={masterPage}
            totalPages={masterTotalPages}
            entriesPerPage={masterEntriesPerPage}
            onPageChange={setMasterPage}
            onEntriesChange={(n) => { setMasterEntriesPerPage(n); setMasterPage(1); }}
            />
         
         <div className="section-spacer">
            <GroupTable
              title="Master alert groups"
              groups={paginatedMasterGroups}
              onDelete={handleDeleteAlertGroup}
            />
          </div>
        </>
      )}

      {/* Alert groups */}
      {showAlert && (
        <>
          <div className="section-header">
            <span className="section-title">Alert groups</span>
            <button className="btn-create" onClick={() => window.location.href = '/admin/alert_group_edit.php'}>
              + New Alert Group
            </button>
          </div>
          <div className="meta-title">
            Group name details — Total groups: {filteredAlertGroups.length}
          </div>
          <Pagination
            page={alertPage}
            totalPages={totalPages}
            entriesPerPage={entriesPerPage}
            onPageChange={setAlertPage}
            onEntriesChange={(n) => { setEntriesPerPage(n); setAlertPage(1); }}
          />
          <GroupTable
            title="Alert groups"
            groups={paginatedAlertGroups}
            onDelete={handleDeleteAlertGroup}
          />
        </>
      )}
      </div>
    </>
  );
}